// =============================================================================
// princess-castle — b05 "Malika" guest-page variant.
//
// SUPER MAXIMALIST princess fairy-tale castle birthday for a 5-year-old
// (Sevara malika). Hero is a full-bleed cotton-candy pink sky with a giant
// castle silhouette behind, GIANT Pinyon Script gold names, hovering jeweled
// tiara, photo in heart frame, and a "SEHRGARLAR DUNYOSIGA XUSH KELIBSIZ"
// banner. The whole page is dusted with slowly drifting fairy-dust particles
// that sparkle on/off in 8s loops.
//
// Animation signatures (all gated by prefers-reduced-motion):
//   * FAIRY DUST: 36 particles drift up + sideways across the entire hero,
//     sparkling on/off on 8s loops.
//   * Stars TWINKLE: random opacity changes.
//   * Castle pennants WAVE: small rotation oscillation (-6° / +6°).
//   * Tiara HOVERS: gentle 4px vertical bobbing on a 3s loop.
//
// Sound triggers (5, gated by useSound):
//   1. RSVP "yes" submit → chime.
//   2. Tiara hover/glow → ding (one-shot per hover).
//   3. Page-mount fairy-dust signature → swoosh.
//   4. Schedule item tap → tap.
//   5. Saroy ahli card click → click.
//
// Music: a soft fairy-tale waltz pad — useAmbient on a triangle-wave A-major
// triad, very low master gain. Toggled by a button in its own section.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
  GuestPhoto,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import type { VariantProps } from "./types";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";

// ─── Fairy-dust particle field ───────────────────────────────────────────
interface DustParticle {
  id: number;
  left: number;       // %
  size: number;       // px
  delay: number;      // s (negative = mid-loop)
  duration: number;   // s
  driftX: number;     // px sideways
  hue: 0 | 1;         // 0 = gold, 1 = pink
  variant: 0 | 1 | 2; // keyframe variant
}

function buildDust(n: number): DustParticle[] {
  const rng = (seed: number) => {
    const x = Math.sin(seed * 9301 + 49297) * 233280;
    return x - Math.floor(x);
  };
  return Array.from({ length: n }).map((_, i) => ({
    id: i,
    left: rng(i + 1) * 100,
    size: 3 + rng(i + 200) * 7,
    delay: -rng(i + 400) * 8,
    duration: 7 + rng(i + 500) * 4,
    driftX: (rng(i + 600) - 0.5) * 80,
    hue: (i % 3 === 0 ? 1 : 0) as 0 | 1,
    variant: (i % 3) as 0 | 1 | 2,
  }));
}

// ─── Reveal hook (IntersectionObserver) ──────────────────────────────────
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setShown(true);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.18 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}

// ─── Tiara SVG (jeweled, used in hero) ───────────────────────────────────
function Tiara({ accent, ink, size = 140, glow = false }: { accent: string; ink: string; size?: number; glow?: boolean }) {
  const goldDeep = "#B7942A";
  const ribbonPink = "#E689A0";
  return (
    <svg
      aria-hidden
      viewBox="0 0 100 56"
      width={size}
      height={size * (56 / 100)}
      style={{
        filter: glow
          ? `drop-shadow(0 0 14px ${accent}cc) drop-shadow(0 0 28px ${ribbonPink}99)`
          : `drop-shadow(0 4px 6px ${ink}33)`,
        transition: "filter 280ms ease",
      }}
    >
      <path d="M 8 38 Q 50 16 92 38" fill="none" stroke={accent} strokeWidth="2" />
      <path d="M 8 38 Q 50 16 92 38" fill="none" stroke={goldDeep} strokeWidth="0.8" />
      <path d="M 22 32 L 22 8 L 32 30 Z" fill={accent} stroke={goldDeep} strokeWidth="0.6" />
      <path d="M 40 24 L 50 2 L 60 24 Z" fill={accent} stroke={goldDeep} strokeWidth="0.6" />
      <path d="M 68 30 L 78 8 L 78 32 Z" fill={accent} stroke={goldDeep} strokeWidth="0.6" />
      <circle cx="50" cy="14" r="4.5" fill={ribbonPink} stroke={accent} strokeWidth="0.8" />
      <circle cx="50" cy="12" r="1.4" fill="#FFF8D6" />
      <circle cx="22" cy="14" r="2.3" fill={ribbonPink} stroke={accent} strokeWidth="0.6" />
      <circle cx="78" cy="14" r="2.3" fill={ribbonPink} stroke={accent} strokeWidth="0.6" />
      {[14, 28, 38, 62, 72, 86].map((px, k) => (
        <circle key={k} cx={px} cy={36} r="1.2" fill="#FFF8D6" stroke={accent} strokeWidth="0.4" />
      ))}
    </svg>
  );
}

// ─── Big castle backdrop SVG (hero) ──────────────────────────────────────
function CastleBackdrop({ accent, ink, opacity = 0.22 }: { accent: string; ink: string; opacity?: number }) {
  const goldDeep = "#B7942A";
  return (
    <svg
      aria-hidden
      viewBox="0 0 600 240"
      preserveAspectRatio="xMidYMax meet"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "60%",
        opacity,
        pointerEvents: "none",
      }}
    >
      <rect x="80" y="170" width="440" height="70" fill={ink} />
      {Array.from({ length: 22 }, (_, k) => (
        <rect key={k} x={80 + k * 21} y="160" width="14" height="12" fill={ink} />
      ))}
      <path d="M 270 240 L 270 200 Q 300 160 330 200 L 330 240 Z" fill="#3A1F2E" />
      <path d="M 270 240 L 270 200 Q 300 160 330 200 L 330 240 Z" fill="none" stroke={accent} strokeWidth="1.2" />
      {[120, 165, 210, 390, 435, 480].map((wx, k) => (
        <rect key={k} x={wx} y="195" width="14" height="22" fill={accent} opacity="0.8" />
      ))}

      {/* Left turret */}
      <rect x="60" y="100" width="50" height="80" fill={ink} />
      {Array.from({ length: 4 }, (_, k) => (
        <rect key={k} x={60 + k * 13} y="90" width="10" height="12" fill={ink} />
      ))}
      <path d="M 50 100 L 85 16 L 120 100 Z" fill={accent} stroke={goldDeep} strokeWidth="1" />
      <line x1="58" y1="80" x2="112" y2="80" stroke={goldDeep} strokeWidth="0.8" />
      <line x1="64" y1="60" x2="106" y2="60" stroke={goldDeep} strokeWidth="0.8" />
      <line x1="70" y1="42" x2="100" y2="42" stroke={goldDeep} strokeWidth="0.8" />
      <g style={{ transformOrigin: "85px 16px" }} className="pc-pennant">
        <line x1="85" y1="16" x2="85" y2="-6" stroke={accent} strokeWidth="1.6" />
        <path d="M 85 -6 L 110 0 L 85 6 Z" fill={accent} />
      </g>
      <rect x="78" y="120" width="14" height="22" rx="7" fill={accent} opacity="0.85" />

      {/* Centre turret (taller) */}
      <rect x="245" y="80" width="110" height="100" fill={ink} />
      {Array.from({ length: 8 }, (_, k) => (
        <rect key={k} x={245 + k * 14} y="68" width="10" height="14" fill={ink} />
      ))}
      <path d="M 235 80 L 300 -16 L 365 80 Z" fill={accent} stroke={goldDeep} strokeWidth="1.2" />
      <path d="M 245 80 Q 257 70 270 80 Q 285 70 300 80 Q 315 70 330 80 Q 345 70 355 80" fill="none" stroke={goldDeep} strokeWidth="1" />
      <line x1="248" y1="56" x2="352" y2="56" stroke={goldDeep} strokeWidth="0.8" />
      <line x1="258" y1="32" x2="342" y2="32" stroke={goldDeep} strokeWidth="0.8" />
      <line x1="270" y1="8" x2="330" y2="8" stroke={goldDeep} strokeWidth="0.8" />
      <g style={{ transformOrigin: "300px -16px" }} className="pc-pennant pc-pennant-2">
        <line x1="300" y1="-16" x2="300" y2="-50" stroke={accent} strokeWidth="2" />
        <path d="M 300 -50 L 340 -42 L 300 -34 Z" fill={accent} />
      </g>
      <circle cx="300" cy="-16" r="3" fill={accent} />
      <path d="M 282 130 L 282 110 Q 300 90 318 110 L 318 130 Z" fill={accent} opacity="0.9" />
      <line x1="300" y1="92" x2="300" y2="130" stroke={ink} strokeWidth="1" />

      {/* Right turret */}
      <rect x="490" y="110" width="50" height="70" fill={ink} />
      {Array.from({ length: 4 }, (_, k) => (
        <rect key={k} x={490 + k * 13} y="100" width="10" height="12" fill={ink} />
      ))}
      <path d="M 480 110 L 515 26 L 550 110 Z" fill={accent} stroke={goldDeep} strokeWidth="1" />
      <line x1="488" y1="90" x2="542" y2="90" stroke={goldDeep} strokeWidth="0.8" />
      <line x1="494" y1="70" x2="536" y2="70" stroke={goldDeep} strokeWidth="0.8" />
      <line x1="500" y1="52" x2="530" y2="52" stroke={goldDeep} strokeWidth="0.8" />
      <g style={{ transformOrigin: "515px 26px" }} className="pc-pennant pc-pennant-3">
        <line x1="515" y1="26" x2="515" y2="4" stroke={accent} strokeWidth="1.6" />
        <path d="M 515 4 L 540 10 L 515 16 Z" fill={accent} />
      </g>
      <rect x="508" y="130" width="14" height="22" rx="7" fill={accent} opacity="0.85" />
    </svg>
  );
}

// ─── Heart-framed photo (hero) ───────────────────────────────────────────
function HeartPhoto({ src, accent, bg, size = 240 }: { src?: string; accent: string; bg: string; size?: number }) {
  const goldDeep = "#B7942A";
  // Heart path on a 220px-square plane (natural pixel coords for clip-path)
  const heartPath = "M110 200 C 30 144 6 92 30 48 C 56 14 90 22 110 50 C 130 22 164 14 190 48 C 214 92 190 144 110 200 Z";
  return (
    <div style={{ position: "relative", width: size, height: size * 0.92 }}>
      <svg viewBox="0 0 100 92" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        {/* Outer rim — bigger heart in gold */}
        <path d="M50 90 C 14 64 2 38 16 18 C 26 4 42 8 50 20 C 58 8 74 4 84 18 C 98 38 86 64 50 90 Z" fill={accent} />
        <path d="M50 88 C 16 64 4 40 18 20 C 28 8 42 10 50 22 C 58 10 72 8 82 20 C 96 40 84 64 50 88 Z" fill="none" stroke={goldDeep} strokeWidth="0.8" />
        {/* Top jewel */}
        <circle cx="50" cy="18" r="3" fill="#E689A0" stroke={accent} strokeWidth="0.7" />
        <circle cx="50" cy="17" r="0.9" fill="#FFF8D6" />
      </svg>
      {/* Real photo, clipped to inner heart shape */}
      <div
        style={{
          position: "absolute",
          inset: "4%",
          clipPath: `path('${heartPath}')`,
          WebkitClipPath: `path('${heartPath}')`,
          background: `radial-gradient(circle at 40% 30%, #FFD0DD, #F7B6C7)`,
          overflow: "hidden",
        }}
      >
        <GuestPhoto
          src={src}
          style={{ width: "100%", height: "100%" }}
          fallbackBg={bg}
          fallbackTone={accent}
        />
      </div>
    </div>
  );
}

// ─── Saroy ahli (court roles) ────────────────────────────────────────────
interface CourtMember {
  realName: string;
  role: string;
  storyLine: string;
  emoji: string;
}

const COURT: CourtMember[] = [
  { realName: "Sevara malika", role: "Zolushka — Bosh malika", emoji: "👸", storyLine: "Bugungi tantananing yulduzi. Shisha tufli orzusi bilan yashaydi." },
  { realName: "Mironbek shahzoda", role: "Zolushkaning shahzodasi", emoji: "🤴", storyLine: "Tantananing barcha raqslarida ishtirok etadi. Sevaraning katta akasi." },
  { realName: "Madina opa", role: "Sehrgar — Fairy Godmother", emoji: "🧚", storyLine: "Sehrli tayoqcha bilan butun zalni ertakka aylantirgan amma." },
  { realName: "Aziza xola", role: "Qor malikasi — Elsa", emoji: "❄️", storyLine: "Muzdek desert mahorati bilan mashhur, ovozi qo'shiqday xolajon." },
  { realName: "Asilbek bobo", role: "Saltanat qiroli", emoji: "👑", storyLine: "Saroyni asragan bobojon. Ertaklarning eng yaxshi hikoyachisi." },
  { realName: "Munisaxon buvi", role: "Mehrli qirolicha", emoji: "💝", storyLine: "Sevaraga shisha tufli haqida ertak aytib beradigan buvijon." },
];

// ─── Sehrli faoliyatlar ──────────────────────────────────────────────────
const ACTIVITIES: Array<{ time: string; title: string; desc: string }> = [
  { time: "14:00", title: "Sehrli tayoqcha yasash", desc: "Har bir mehmon o'ziga sehrli tayoqcha yasaydi — yulduz, lenta va yaltiroqlar bilan." },
  { time: "14:30", title: "Malikalar paradi", desc: "Barcha malika va shahzodalar zal bo'ylab tantanali yurish qilishadi. Tojlar mahkam!" },
  { time: "15:00", title: "Saroy izlash o'yini", desc: "Yashirilgan sehrli toshlarni topish kerak — Zolushkaning yo'qolgan tufliga ishora." },
  { time: "15:30", title: "Choy marosimi", desc: "Haqiqiy malika choyxonasida shirin pechene va meva bilan choy ichiladi." },
  { time: "16:00", title: "Tort marosimi", desc: "Besh sham — besh tilak. Sevara malikaning eng katta hayotiy lahzasi." },
];

const SCHEDULE_ITEMS: Array<[string, string]> = [
  ["13:30", "Saroyga qirollik kelishi — mehmonlarni Madina sehrgar kutib oladi"],
  ["14:00", "Tantanali ochilish marosimi — Sevara malika va shahzoda paradini boshlaydi"],
  ["14:30", "Sehrli tayoqcha ustaxonasi — har bir mehmonga o'ziga xos tayoqcha"],
  ["15:30", "Qirollik ziyofati — palov, shirinliklar va malika cho'zmasi"],
  ["16:00", "Sham marosimi — beshta sham, beshta tilak, bitta orzu"],
  ["16:30", "Sovg'a topshirish va saroy fotosessiya"],
  ["17:00", "Xayrlashuv — har bir mehmonga sehrli xotira sumkasi"],
];

// ─── Saroy ahli section ──────────────────────────────────────────────────
interface CourtSectionProps {
  t: VariantProps["t"];
  ribbonPink: string;
  goldDeep: string;
  members: CourtMember[];
  onCardClick: () => void;
}

function CourtSection({ t, ribbonPink, goldDeep, members, onCardClick }: CourtSectionProps) {
  const reveal = useReveal<HTMLElement>();
  return (
    <section
      ref={reveal.ref}
      className="pc-section"
      style={{
        padding: "120px 32px",
        borderTop: `1px solid ${t.accent}33`,
        background: `linear-gradient(180deg, ${t.bg} 0%, ${ribbonPink}18 100%)`,
        position: "relative",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 700, letterSpacing: "0.42em", color: t.accent, marginBottom: 14 }}>
          ✦ SAROY AHLI ✦
        </div>
        <h2 style={{ fontFamily: t.font, fontStyle: "italic", fontSize: "clamp(48px, 7vw, 84px)", fontWeight: 400, color: t.ink, marginBottom: 14 }}>
          Tantanamiz qahramonlari
        </h2>
        <p style={{ fontFamily: "var(--font-body)", fontSize: 17, fontStyle: "italic", color: `${t.ink}cc`, maxWidth: 620, margin: "0 auto 56px" }}>
          Bugun har kim o'z ertak rolini olib keladi. Tojlarni mahkam tuting!
        </p>

        <div
          className="pc-court-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 24,
          }}
        >
          {members.map((m, i) => (
            <button
              key={m.realName}
              onClick={onCardClick}
              type="button"
              className="pc-court-card"
              style={{
                textAlign: "center",
                padding: "32px 22px 26px",
                background: t.bg,
                border: `2px solid ${t.accent}`,
                borderRadius: 18,
                position: "relative",
                opacity: reveal.shown ? 1 : 0,
                transform: reveal.shown ? "translateY(0)" : "translateY(16px)",
                transition: `opacity 600ms ease ${i * 90}ms, transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 90}ms`,
                boxShadow: `0 4px 0 -1px ${goldDeep}55, 0 10px 22px ${ribbonPink}33`,
                cursor: "pointer",
                color: t.ink,
                fontFamily: "inherit",
              }}
              aria-label={`${m.realName} — ${m.role}`}
            >
              <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: t.bg, padding: "2px 10px", borderRadius: 999, border: `1.5px solid ${t.accent}` }}>
                <span style={{ fontSize: 22 }}>{m.emoji}</span>
              </div>
              <div style={{ marginTop: 14, fontFamily: t.font, fontStyle: "italic", fontSize: 28, fontWeight: 400, color: t.accent, lineHeight: 1.1 }}>
                {m.realName}
              </div>
              <div style={{ marginTop: 6, fontFamily: "var(--font-ui)", fontSize: 10, fontWeight: 700, letterSpacing: "0.32em", color: t.ink, textTransform: "uppercase" }}>
                {m.role}
              </div>
              <div style={{ height: 1, width: 60, background: t.accent, opacity: 0.5, margin: "16px auto" }} />
              <div style={{ fontFamily: "var(--font-body)", fontSize: 14, fontStyle: "italic", color: `${t.ink}cc`, lineHeight: 1.5 }}>
                {m.storyLine}
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Sehrli faoliyatlar section ──────────────────────────────────────────
interface ActivitiesSectionProps {
  t: VariantProps["t"];
  ribbonPink: string;
  goldDeep: string;
  items: Array<{ time: string; title: string; desc: string }>;
}

function ActivitiesSection({ t, ribbonPink, goldDeep, items }: ActivitiesSectionProps) {
  const reveal = useReveal<HTMLElement>();
  return (
    <section
      ref={reveal.ref}
      className="pc-section"
      style={{
        padding: "120px 32px",
        borderTop: `1px solid ${t.accent}33`,
        background: `${t.accent}08`,
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 700, letterSpacing: "0.42em", color: t.accent, marginBottom: 14 }}>
          ✦ SEHRLI FAOLIYATLAR ✦
        </div>
        <h2 style={{ fontFamily: t.font, fontStyle: "italic", fontSize: "clamp(48px, 7vw, 84px)", fontWeight: 400, color: t.ink, marginBottom: 14 }}>
          Saroydagi sehrlar
        </h2>
        <p style={{ fontFamily: "var(--font-body)", fontSize: 17, fontStyle: "italic", color: `${t.ink}cc`, maxWidth: 620, margin: "0 auto 56px" }}>
          Bugungi tantanani sehrli qiladigan beshta marosim — har bir mehmon ishtirok etadi.
        </p>

        <div
          className="pc-act-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 22,
          }}
        >
          {items.map((it, i) => (
            <div
              key={it.title}
              className="pc-card"
              style={{
                position: "relative",
                padding: "28px 22px",
                background: t.bg,
                border: `1.5px solid ${t.accent}`,
                borderRadius: 14,
                textAlign: "left",
                boxShadow: `0 6px 0 -2px ${ribbonPink}55, 0 12px 22px ${t.accent}22`,
                opacity: reveal.shown ? 1 : 0,
                transform: reveal.shown ? "translateY(0)" : "translateY(20px)",
                transition: `opacity 600ms ease ${i * 100}ms, transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 100}ms`,
              }}
            >
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: -16,
                  right: -10,
                  width: 36,
                  height: 36,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  background: t.accent,
                  color: t.bg,
                  fontSize: 18,
                  boxShadow: `0 0 0 4px ${t.bg}, 0 4px 10px ${goldDeep}66`,
                }}
              >
                ✦
              </div>
              <div
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  background: ribbonPink,
                  color: t.bg,
                  borderRadius: 999,
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.24em",
                  marginBottom: 12,
                }}
              >
                {it.time}
              </div>
              <div style={{ fontFamily: t.font, fontStyle: "italic", fontSize: 28, color: t.accent, fontWeight: 400, lineHeight: 1.15, marginBottom: 8 }}>
                {it.title}
              </div>
              <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: `${t.ink}cc`, lineHeight: 1.55 }}>
                {it.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Main variant ────────────────────────────────────────────────────────
// The fairy-waltz spec is hoisted to module scope so the `useAmbient` hook's
// dependency array sees a stable reference (no re-init on every render).
const FAIRY_WALTZ_FREQS: number[] = [220.0, 277.18, 329.63, 440.0];

export function PrincessCastleVariant({ t, isDark, ctx }: VariantProps) {
  const { play } = useSound();
  const reduce = useReducedMotion();
  const photos = t.photos || [];

  // Derive numeric age from t.names ("Sevara malika 5 yosh") with default 5.
  const ageMatch = t.names.match(/(\d{1,3})\s*(yosh|yil)?\s*$/i);
  const ageNumber = ageMatch ? ageMatch[1] : "5";
  const cleanName = t.names.replace(/\s*[·•]?\s*\d+\s*(yosh|yil)?\s*$/i, "").trim() || t.names;

  const goldDeep = "#B7942A";
  const ribbonPink = "#E689A0";
  const pinkMid = "#FFD0DD";
  const pinkDeep = "#F7B6C7";

  // Build dust particles deterministically (kept across renders)
  const dustRef = useRef<DustParticle[]>(buildDust(36));
  const dust = dustRef.current;

  // Page-mount swoosh (signature)
  const swooshFiredRef = useRef(false);
  useEffect(() => {
    if (swooshFiredRef.current) return;
    swooshFiredRef.current = true;
    const id = window.setTimeout(() => play("swoosh"), 280);
    return () => window.clearTimeout(id);
  }, [play]);

  // Tiara hover state — visual glow + ding sound (one-shot per hover)
  const [tiaraGlow, setTiaraGlow] = useState(false);
  const tiaraDingFired = useRef(false);
  const onTiaraEnter = () => {
    setTiaraGlow(true);
    if (tiaraDingFired.current) return;
    tiaraDingFired.current = true;
    play("ding");
  };
  const onTiaraLeave = () => {
    setTiaraGlow(false);
    tiaraDingFired.current = false;
  };

  // Music — soft fairy-tale waltz pad (A-major triad)
  const [musicOn, setMusicOn] = useState(false);
  useAmbient(musicOn, { freqs: FAIRY_WALTZ_FREQS, type: "triangle", master: 0.035, lfo: 0.1 });

  // RSVP wrapper — chime on yes
  const wrappedSetRsvp = (v: string | null) => {
    if (v === "yes") play("chime");
    ctx.rsvp.setRsvp(v);
  };

  // Saroy ahli card click handler
  const onCourtClick = () => play("click");

  const dateLine = useMemo(() => {
    const months = ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avgust", "sentabr", "oktabr", "noyabr", "dekabr"];
    const [dd, mm, yyyy] = t.date.split(".");
    const idx = Math.max(0, Math.min(11, parseInt(mm, 10) - 1));
    return `${parseInt(dd, 10)}-${months[idx]} ${yyyy}`;
  }, [t.date]);

  return (
    <div className="pc-variant v-b05" style={{ position: "relative", overflowX: "hidden" }}>
      <style>{`
        @keyframes pcDust0 {
          0%   { transform: translate3d(0, 0, 0) scale(0.7); opacity: 0; }
          15%  { opacity: 0.95; }
          50%  { transform: translate3d(calc(var(--pc-dx) * 0.5), -45vh, 0) scale(1); opacity: 0.6; }
          100% { transform: translate3d(var(--pc-dx), -110vh, 0) scale(0.7); opacity: 0; }
        }
        @keyframes pcDust1 {
          0%   { transform: translate3d(0, 0, 0) scale(0.6); opacity: 0; }
          25%  { opacity: 1; }
          70%  { opacity: 0.4; }
          100% { transform: translate3d(calc(var(--pc-dx) * -1), -110vh, 0) scale(0.5); opacity: 0; }
        }
        @keyframes pcDust2 {
          0%   { transform: translate3d(0, 0, 0); opacity: 0; }
          10%  { opacity: 0.9; }
          40%  { opacity: 0.2; }
          70%  { opacity: 0.95; }
          100% { transform: translate3d(var(--pc-dx), -110vh, 0); opacity: 0; }
        }
        @keyframes pcTwinkle {
          0%, 100% { opacity: 0.35; }
          50%      { opacity: 1; }
        }
        @keyframes pcPennantWave {
          0%, 100% { transform: rotate(-6deg); }
          50%      { transform: rotate(6deg); }
        }
        @keyframes pcTiaraHover {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-4px); }
        }
        @keyframes pcHeartGlow {
          0%, 100% { box-shadow: 0 12px 32px ${ribbonPink}55, 0 0 0 0 ${t.accent}55; }
          50%      { box-shadow: 0 16px 40px ${ribbonPink}88, 0 0 0 12px ${t.accent}22; }
        }
        @keyframes pcButterfly {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25%      { transform: translate(8px, -12px) rotate(8deg); }
          50%      { transform: translate(0, -6px) rotate(-3deg); }
          75%      { transform: translate(-8px, -10px) rotate(6deg); }
        }
        .pc-tiara-wrap { animation: pcTiaraHover 3s ease-in-out infinite; will-change: transform; display: inline-block; }
        .pc-pennant { animation: pcPennantWave 2.6s ease-in-out infinite; transform-box: fill-box; }
        .pc-pennant-2 { animation-duration: 3.2s; animation-delay: 0.3s; }
        .pc-pennant-3 { animation-duration: 2.8s; animation-delay: 0.6s; }
        .pc-twinkle { animation: pcTwinkle 2.4s ease-in-out infinite; }
        .pc-butterfly { animation: pcButterfly 6s ease-in-out infinite; transform-origin: center; }
        .pc-card { transition: transform 220ms ease, box-shadow 220ms ease; }
        .pc-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px ${t.accent}44; }
        .pc-court-card { cursor: pointer; transition: opacity 600ms ease, transform 220ms ease, box-shadow 220ms ease; }
        .pc-court-card:hover { transform: translateY(-3px) rotate(-0.6deg) !important; box-shadow: 0 14px 32px ${ribbonPink}66; }
        .pc-schedule-wrap > section { transition: background 220ms ease; }
        @media (prefers-reduced-motion: reduce) {
          .pc-dust, .pc-pennant, .pc-tiara-wrap, .pc-twinkle, .pc-butterfly { animation: none !important; }
        }
        @media (max-width: 768px) {
          .pc-hero { padding: 64px 18px 48px !important; }
          .pc-names { font-size: clamp(56px, 14vw, 96px) !important; }
          .pc-eyebrow { font-size: 9px !important; letter-spacing: 0.32em !important; }
          /* Date/venue pill: allow wrapping and shrink so it fits 390px */
          .pc-meta {
            font-size: 10px !important;
            letter-spacing: 0.14em !important;
            padding: 10px 14px !important;
            gap: 6px !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
            max-width: min(340px, 90vw) !important;
            border-radius: 999px !important;
          }
          .pc-section { padding: 64px 18px !important; }
          .pc-court-grid, .pc-act-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .v-b05 button, .v-b05 a[role="button"] { min-height: 44px; }
          /* Tiara size: scale down on phones */
          .pc-tiara-wrap svg { width: 100px !important; height: auto !important; }
          /* Heart photo: cap width so it fits on small phones;
             the inner 240px-wide HeartPhoto div is overflowHidden to keep it inside */
          .pc-hero .pc-heart-photo {
            width: min(180px, 54vw) !important;
            overflow: hidden !important;
          }
          .pc-hero .pc-heart-photo > div {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 100/92 !important;
          }
          /* Heart countdown cards: smaller on mobile */
          .pc-card[style*="width: 150px"] {
            width: min(130px, 38vw) !important;
            height: min(130px, 38vw) !important;
          }
        }
        .v-b05 button:focus-visible,
        .v-b05 a:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 3px;
        }
        .pc-tiara:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 4px;
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO — Full-bleed pink sky · castle · tiara · names · heart photo
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        className="pc-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "100px 32px 140px",
          textAlign: "center",
          background: `linear-gradient(180deg, #FFF4F8 0%, ${t.bg} 32%, ${pinkMid} 70%, ${pinkDeep} 100%)`,
          color: t.ink,
          overflow: "hidden",
        }}
      >
        {/* Twinkling stars layer */}
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            {Array.from({ length: 36 }).map((_, i) => {
              const seed = (i + 1) * 17.7;
              const x = (Math.sin(seed) * 0.5 + 0.5) * 100;
              const y = (Math.cos(seed * 1.3) * 0.5 + 0.5) * 80;
              const s = 0.6 + ((i % 5) * 0.25);
              const delay = (i % 7) * 0.4;
              return (
                <g
                  key={i}
                  className="pc-twinkle"
                  style={{ animationDelay: `${delay}s` } as CSSProperties}
                >
                  <circle cx={x} cy={y} r={s * 0.4} fill={t.accent} />
                  {i % 3 === 0 && (
                    <path
                      d={`M${x} ${y - s * 1.6} L${x + s * 0.3} ${y - s * 0.3} L${x + s * 1.6} ${y} L${x + s * 0.3} ${y + s * 0.3} L${x} ${y + s * 1.6} L${x - s * 0.3} ${y + s * 0.3} L${x - s * 1.6} ${y} L${x - s * 0.3} ${y - s * 0.3} Z`}
                      fill={t.accent}
                      opacity={0.85}
                    />
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Big castle silhouette behind */}
        <CastleBackdrop accent={t.accent} ink={t.ink} opacity={0.22} />

        {/* Butterflies (3) flying across the hero */}
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {[
            { x: 12, y: 22, color: t.accent, scale: 1.0, delay: 0 },
            { x: 78, y: 36, color: ribbonPink, scale: 0.9, delay: 1.1 },
            { x: 64, y: 64, color: t.accent, scale: 0.85, delay: 2.2 },
          ].map((b, i) => (
            <svg
              key={i}
              viewBox="0 0 24 24"
              className="pc-butterfly"
              aria-hidden
              style={{
                position: "absolute",
                left: `${b.x}%`,
                top: `${b.y}%`,
                width: 36 * b.scale,
                height: 36 * b.scale,
                animationDelay: `${b.delay}s`,
              }}
            >
              <path d="M 12 12 Q 4 4 4 12 Q 4 18 12 14 Q 20 18 20 12 Q 20 4 12 12 Z" fill={b.color} opacity="0.85" />
              <ellipse cx="12" cy="12" rx="0.6" ry="3" fill={t.ink} />
              <circle cx="6" cy="9" r="0.6" fill="#FFF8D6" />
              <circle cx="18" cy="9" r="0.6" fill="#FFF8D6" />
            </svg>
          ))}
        </div>

        {/* Fairy-dust particle field (entire hero) */}
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
          {dust.map((p) => (
            <span
              key={p.id}
              className="pc-dust"
              style={{
                position: "absolute",
                bottom: "-10vh",
                left: `${p.left}%`,
                width: p.size,
                height: p.size,
                borderRadius: "50%",
                background:
                  p.hue === 0
                    ? `radial-gradient(circle, #FFF8D6, ${t.accent} 60%, ${goldDeep} 100%)`
                    : `radial-gradient(circle, #FFF4F8, ${ribbonPink} 60%, #C95C7A 100%)`,
                boxShadow: p.hue === 0 ? `0 0 6px ${t.accent}aa` : `0 0 6px ${ribbonPink}aa`,
                willChange: "transform, opacity",
                animation: reduce
                  ? "none"
                  : `pcDust${p.variant} ${p.duration}s ${p.variant === 1 ? "ease-in-out" : "linear"} ${p.delay}s infinite`,
                opacity: reduce ? 0.55 : undefined,
                ["--pc-dx" as string]: `${p.driftX}px`,
              } as CSSProperties}
            />
          ))}
        </div>

        {/* Hero content */}
        <div style={{ position: "relative", zIndex: 2, maxWidth: 880, margin: "0 auto" }}>
          <div
            className="pc-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.ink,
              opacity: 0.8,
              marginBottom: 18,
            }}
          >
            ✦ SEHRGARLAR DUNYOSIGA XUSH KELIBSIZ ✦
          </div>

          {/* Tiara — hovers + glows on mouseenter */}
          <div
            className="pc-tiara-wrap pc-tiara"
            onMouseEnter={onTiaraEnter}
            onMouseLeave={onTiaraLeave}
            onFocus={onTiaraEnter}
            onBlur={onTiaraLeave}
            tabIndex={0}
            role="img"
            aria-label="Sehrli toj"
            style={{ marginBottom: 6, cursor: "pointer" }}
          >
            <Tiara accent={t.accent} ink={t.ink} size={140} glow={tiaraGlow} />
          </div>

          {/* GIANT names */}
          <h1
            className="pc-names"
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontStyle: "italic",
              fontSize: "clamp(80px, 14vw, 200px)",
              fontWeight: 400,
              lineHeight: 0.95,
              color: t.accent,
              marginBottom: 10,
              textShadow: `0 2px 0 ${goldDeep}88, 0 0 18px ${t.accent}55, 0 4px 22px ${ribbonPink}55`,
              letterSpacing: "0.005em",
            }}
          >
            {cleanName}
          </h1>

          {/* Age subline */}
          <div
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontStyle: "italic",
              fontSize: "clamp(28px, 4vw, 44px)",
              color: t.ink,
              marginBottom: 22,
            }}
          >
            {ageNumber} yosh
          </div>

          {/* Heart photo with soft glow */}
          <div
            className="pc-heart-photo"
            style={{
              display: "inline-block",
              marginBottom: 28,
              animation: reduce ? "none" : "pcHeartGlow 3.4s ease-in-out infinite",
              borderRadius: 16,
            }}
          >
            <HeartPhoto src={photos[0]} accent={t.accent} bg={t.bg} size={240} />
          </div>

          {/* Date + venue card */}
          <div
            className="pc-meta"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 14,
              padding: "14px 28px",
              background: t.bg,
              border: `1.5px solid ${t.accent}`,
              borderRadius: 999,
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: t.ink,
              textTransform: "uppercase",
              boxShadow: `0 6px 0 -2px ${t.accent}55, 0 12px 24px ${ribbonPink}33`,
            }}
          >
            <span style={{ color: t.accent }}>♛</span>
            <span>{dateLine}</span>
            <span style={{ color: t.accent }}>·</span>
            <span style={{ textTransform: "none", letterSpacing: "0.16em" }}>{t.venue}</span>
            <span style={{ color: t.accent }}>♛</span>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          COUNTDOWN — heart-shaped boxes with gold borders
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        className="pc-section"
        style={{
          padding: "100px 32px",
          borderTop: `1px solid ${t.accent}33`,
          background: `${t.accent}10`,
          textAlign: "center",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 700, letterSpacing: "0.42em", color: t.accent, marginBottom: 16 }}>
            ✦ TANTANAGACHA ✦
          </div>
          <h2 style={{ fontFamily: t.font, fontStyle: "italic", fontSize: "clamp(48px, 7vw, 80px)", fontWeight: 400, color: t.ink, marginBottom: 32 }}>
            Sehrli soatlar
          </h2>
          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 18 }}>
            {[
              [ctx.countdown.days, "kun"],
              [ctx.countdown.hours, "soat"],
              [ctx.countdown.mins, "daqiqa"],
              [ctx.countdown.secs, "soniya"],
            ].map((cell) => {
              const n = cell[0] as number;
              const lbl = cell[1] as string;
              return (
                <div
                  key={lbl}
                  className="pc-card"
                  style={{
                    width: 150,
                    height: 150,
                    position: "relative",
                    display: "grid",
                    placeItems: "center",
                    textAlign: "center",
                  }}
                >
                  <svg
                    viewBox="0 0 100 92"
                    preserveAspectRatio="none"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                    aria-hidden
                  >
                    <path
                      d="M50 86 C 18 64 6 40 18 22 C 28 8 42 12 50 24 C 58 12 72 8 82 22 C 94 40 82 64 50 86 Z"
                      fill={t.bg}
                      stroke={t.accent}
                      strokeWidth="1.4"
                    />
                    <path
                      d="M50 82 C 22 62 12 42 22 26 C 30 16 42 18 50 28 C 58 18 70 16 78 26 C 88 42 78 62 50 82 Z"
                      fill="none"
                      stroke={goldDeep}
                      strokeWidth="0.6"
                      opacity="0.7"
                    />
                  </svg>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ fontFamily: t.font, fontStyle: "italic", fontSize: 56, color: t.accent, lineHeight: 1, fontWeight: 400 }}>
                      {String(n).padStart(2, "0")}
                    </div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 700, letterSpacing: "0.2em", color: t.ink, marginTop: 6 }}>
                      {lbl.toUpperCase()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hidden CountdownSection — keeps the standard component referenced
          (i18n strings, ICS hooks, etc) without doubling the visual layout. */}
      <div style={{ display: "none" }} aria-hidden>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          STORY — Sevara's princess tale
          ═══════════════════════════════════════════════════════════════════ */}
      <StorySection
        t={t}
        eyebrow="Bizning malikamiz"
        title="Sevara malikaning ertagi"
        body={
          `${cleanName} endigina ${ageNumber} yoshga to'ldi, ammo malikalar dunyosi haqida bilmagan narsasi qolmadi. ` +
          "U Disney malikalarining barcha ismlarini yoddan biladi — Belle, Ariel, Aurora, Zolushka, Elsa — har birining hikoyasini o'zicha qayta aytib bera oladi. " +
          "Bu tantana uchun u uch oy davomida tayyorgarlik ko'rdi: tojini tanladi, mehmonlarini sehrli rollarda taqsimladi, va hatto buvisidan haqiqiy shisha tufli so'radi (buvijon esa shisha emas, kristalldan yasalgan tufli yasab beradigan ustani topdi). " +
          `Bugun saroyimizning eng katta bayrami — kichkina malikamiz ${ageNumber}-bahoriga qadam qo'ymoqda. Sevgi, sehr va ko'rgan ertaklarimizning eng go'zali bilan.`
        }
      />

      {/* ═══════════════════════════════════════════════════════════════════
          SAROY AHLI — fairy-tale roles for invitees
          ═══════════════════════════════════════════════════════════════════ */}
      <CourtSection
        t={t}
        ribbonPink={ribbonPink}
        goldDeep={goldDeep}
        members={COURT}
        onCardClick={onCourtClick}
      />

      {/* ═══════════════════════════════════════════════════════════════════
          SEHRLI FAOLIYATLAR — magical activities
          ═══════════════════════════════════════════════════════════════════ */}
      <ActivitiesSection
        t={t}
        ribbonPink={ribbonPink}
        goldDeep={goldDeep}
        items={ACTIVITIES}
      />

      {/* ═══════════════════════════════════════════════════════════════════
          GALLERY
          ═══════════════════════════════════════════════════════════════════ */}
      <GallerySection t={t} />

      {/* ═══════════════════════════════════════════════════════════════════
          SCHEDULE — wrapped to fire a tap sound on any item interaction
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="pc-schedule-wrap">
        <ScheduleSection t={t} items={SCHEDULE_ITEMS} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          MAP
          ═══════════════════════════════════════════════════════════════════ */}
      <MapSection t={t} isDark={isDark} addressLine="Hyatt Regency Tashkent, Navoiy ko'chasi, Toshkent" />

      {/* ═══════════════════════════════════════════════════════════════════
          MUSIC TOGGLE — fairy-tale waltz pad
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        className="pc-section"
        style={{
          padding: "60px 32px",
          textAlign: "center",
          borderTop: `1px solid ${t.accent}33`,
          background: `${ribbonPink}12`,
        }}
      >
        <MusicToggle
          on={musicOn}
          onToggle={() => {
            const next = !musicOn;
            setMusicOn(next);
            play(next ? "music-on" : "music-off");
          }}
          accent={t.accent}
          ink={t.bg}
        />
        <div style={{ marginTop: 14, fontFamily: "var(--font-body)", fontSize: 13, color: `${t.ink}aa`, fontStyle: "italic" }}>
          Sevara malikaning sevimli vals kuyi
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          RSVP
          ═══════════════════════════════════════════════════════════════════ */}
      <RsvpSection
        t={t}
        isDark={isDark}
        rsvp={ctx.rsvp.rsvp}
        setRsvp={wrappedSetRsvp}
        guests={ctx.rsvp.guests}
        setGuests={ctx.rsvp.setGuests}
        guestName={ctx.rsvp.guestName}
        setGuestName={ctx.rsvp.setGuestName}
        slug={ctx.slug}
        label="Sehrli kunimga keling, malika"
      />

      {/* ═══════════════════════════════════════════════════════════════════
          SHARE + FOOTER
          ═══════════════════════════════════════════════════════════════════ */}
      <ShareSection t={t} />
      <GuestFooter t={t} />
    </div>
  );
}
